import { ReactElement, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import {
  ColumnDTO,
  CustomTableFilterBar,
  DateFilterTemplate,
  ETypes,
  RColumn,
  RemoteTable,
  multiSelectFilterTemplate,
  stringFilterTemplate,
} from 'medere-ui-framework'
import { formatNumberRange, numberRangeFilterTemplate } from '../../../components/novarium/NumberRangeFilter'
import { EMPTY_VALUE, formatIsoMoment, textOrDash } from '../../../components/summary/summaryFormat'
import {
  HospitalizationFilterDTO,
  HospitalizationOptionDTO,
  HospitalizationOptionsDTO,
  HospitalizationRowDTO,
} from '../../../dto/hospitalizationSummary/HospitalizationSummaryDTO'
import {
  HospitalizationTableParams,
  getHospitalizationCensusRows,
  getHospitalizationRows,
} from '../../../services/HospitalizationSummaryController'
import { formatCount } from '../hospitalizationSummaryFormat'
import useHospitalizationGrid from '../useHospitalizationGrid'
import HospitalizationGridNotice from './HospitalizationGridNotice'

/** Clave con la que RemoteTable guarda filtros y orden en el navegador, una por grilla. */
export const LIST_TABLE_ID = 'HospitalizationListTable'
export const CENSUS_TABLE_ID = 'HospitalizationCensusTable'

/**
 * Tipo de dato de cada columna con filtro: define cómo viaja el filtro al backend. Texto: «contiene». Listas: viajan como
 * igualdad (una o varias) por el id de la opción. Fechas, edad y días de estadía: desde / hasta. El sector, el tipo de ingreso y el
 * financiador no son filtros de columna: son los de «Más filtros» de la pantalla.
 */
const FILTERS_TYPE = [
  { field: 'patientName', type: ETypes.string },
  { field: 'documentNumber', type: ETypes.string },
  { field: 'affiliateNumber', type: ETypes.string },
  { field: 'planName', type: ETypes.string },
  { field: 'admissionDiagnosis', type: ETypes.string },
  { field: 'dischargeReasonId', type: ETypes.integer },
  { field: 'age', type: ETypes.integer },
  { field: 'days', type: ETypes.integer },
  { field: 'serviceStart', type: ETypes.dateTime },
  { field: 'serviceEnd', type: ETypes.dateTime },
]

/**
 * Ancho mínimo de cada columna, en px. Son los pesos (`grow`) de las columnas y su suma es el ancho mínimo de la tabla
 * (`tableMinWidth`): si el contenedor es más angosto, la tabla se desplaza hacia el costado en vez de achicar las columnas
 * hasta truncar las cortas (fechas, días, «Internado»); solo el texto largo se trunca con «…» y su tooltip.
 */
const COLUMN_WIDTH = {
  patient: 176,
  start: 144,
  end: 144,
  days: 80,
  location: 208,
  admissionType: 160,
  dischargeReason: 176,
  financier: 160,
  diagnosis: 208,
  // Columnas ocultas (no suman al ancho de la tabla)
  document: 120,
  affiliate: 144,
  plan: 144,
  age: 80,
} as const

/** Las columnas ocultas: no entran en el ancho mínimo de la tabla. */
const HIDDEN_COLUMNS: string[] = ['document', 'affiliate', 'plan', 'age']

const NO_OPTIONS: HospitalizationOptionDTO[] = []
const DEFAULT_SORT = [{ field: 'serviceStart', order: -1 as const }]

/** Texto de cada valor elegido de un filtro de lista (el chip muestra el nombre, no el id). */
function optionNames(options: HospitalizationOptionDTO[]): (id: unknown) => string {
  const names = new Map(options.map((option) => [option.id, option.name]))
  return (id) => (typeof id === 'number' ? names.get(id) ?? '' : '')
}

interface HospitalizationListProps {
  /** `list`: las internaciones que se superponen con el período; `census`: los internados hoy (la misma tabla, acotada). */
  grid: 'list' | 'census'
  /** Filtros aplicados de la pantalla (período y «Más filtros»). */
  filters: HospitalizationFilterDTO
  options: HospitalizationOptionsDTO | null
  /** Lo último que pidió la tabla: es lo que exporta «Exportar». */
  onLoad: (params: HospitalizationTableParams) => void
}

/**
 * Lista de internaciones (y «Internados hoy»): `RemoteTable` con scroll infinito, filtros y orden en el backend. La clave de
 * la fila es el id de la internación. Sin egreso, la fila dice «Internado»; un dato nulo, «—». DNI, N° de afiliado, plan y edad
 * no se ven en la grilla: se buscan y filtran con la barra «+ Filtro» (son columnas ocultas) y salen en el Excel. «Internados
 * hoy» es la misma tabla sin las columnas de egreso, que no tienen nada que decir de quien sigue internado.
 */
export default function HospitalizationList({ grid, filters, options, onLoad }: HospitalizationListProps) {
  const intl = useIntl()
  const isCensus = grid === 'census'
  const load = useCallback(
    (offSet: number, pageSize: number, params: HospitalizationTableParams) =>
      (isCensus ? getHospitalizationCensusRows : getHospitalizationRows)(offSet, pageSize, params),
    [isCensus],
  )
  // «Internados hoy» ignora el período: un cambio de fechas no vuelve a pedir la tabla (sí sector, tipo, financiador y cunas)
  const { sectorId, admissionTypeId, financierId, includeNurseryBeds } = filters
  const censusFilters = useMemo(
    () => filters,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sectorId, admissionTypeId, financierId, includeNurseryBeds],
  )
  const { tableRef, params, loadItems, state, retry } = useHospitalizationGrid<HospitalizationRowDTO>(
    isCensus ? censusFilters : filters,
    load,
    onLoad,
  )

  const rangeLabels = useMemo(() => ({
    from: intl.formatMessage({ id: 'FilterRangeFrom' }),
    to: intl.formatMessage({ id: 'FilterRangeTo' }),
  }), [intl])
  const rangeFilter = useMemo(() => numberRangeFilterTemplate(rangeLabels), [rangeLabels])
  // Días de estadía («desde N» es una estadía larga): enteros, el backend rechaza los decimales
  const daysFilter = useMemo(() => numberRangeFilterTemplate(rangeLabels, { integer: true }), [rangeLabels])

  const dischargeReasons = options?.dischargeReasons ?? NO_OPTIONS
  /** Filtro de lista con varias opciones, en línea y paginado dentro de la barra «Filtro». */
  const reasonFilter = useMemo(() => multiSelectFilterTemplate({
    list: dischargeReasons,
    optionLabel: 'name',
    optionValue: 'id',
    pageSize: true,
  }), [dischargeReasons])

  /**
   * Las columnas que se ven. DNI, N.º de afiliado y edad también son columnas de la tabla, pero quedan ocultas: no se
   * muestran, y la barra «+ Filtro» las ofrece igual para buscar por ellas (datos personales que no ocupan la grilla).
   */
  const visibleColumns = useMemo<ColumnDTO[]>(() => {
    const columns: [string, string][] = [
      ['patientName', 'HospitalizationSummaryColPatient'],
      ['serviceStart', 'HospitalizationSummaryColServiceStart'],
      ...(isCensus ? [] : [['serviceEnd', 'HospitalizationSummaryColServiceEnd'] as [string, string]]),
      ['days', 'HospitalizationSummaryColDays'],
      ['sectorName', 'HospitalizationSummaryColLocation'],
      ['admissionType', 'HospitalizationSummaryColAdmissionType'],
      ...(isCensus ? [] : [['dischargeReasonId', 'HospitalizationSummaryColDischargeReason'] as [string, string]]),
      ['financierName', 'HospitalizationSummaryColFinancier'],
      ['admissionDiagnosis', 'HospitalizationSummaryColAdmissionDiagnosis'],
    ]
    return columns.map(([field, id]) => ({ field, header: intl.formatMessage({ id }) }))
  }, [intl, isCensus])

  // Buscador por paciente y filtros por campo. Los textos de la barra ya vienen en español en el framework
  const filterBar = useMemo<CustomTableFilterBar>(() => ({
    searchField: 'patientName',
    searchLabel: intl.formatMessage({ id: 'HospitalizationSummaryColPatient' }),
    texts: { search: intl.formatMessage({ id: 'HospitalizationSummarySearchPatient' }) },
    fields: [
      { field: 'documentNumber', label: intl.formatMessage({ id: 'HospitalizationSummaryColDocumentNumber' }) },
      { field: 'affiliateNumber', label: intl.formatMessage({ id: 'HospitalizationSummaryColAffiliateNumber' }) },
      { field: 'planName', label: intl.formatMessage({ id: 'HospitalizationSummaryColPlan' }) },
      { field: 'age', label: intl.formatMessage({ id: 'HospitalizationSummaryColAge' }), format: (range) => formatNumberRange(range, rangeLabels) },
      { field: 'admissionDiagnosis', label: intl.formatMessage({ id: 'HospitalizationSummaryColAdmissionDiagnosis' }) },
      { field: 'days', label: intl.formatMessage({ id: 'HospitalizationSummaryColDays' }), format: (range) => formatNumberRange(range, rangeLabels) },
      { field: 'serviceStart', label: intl.formatMessage({ id: 'HospitalizationSummaryFilterServiceStart' }) },
      ...(isCensus ? [] : [
        { field: 'serviceEnd', label: intl.formatMessage({ id: 'HospitalizationSummaryFilterServiceEnd' }) },
        { field: 'dischargeReasonId', label: intl.formatMessage({ id: 'HospitalizationSummaryColDischargeReason' }), format: optionNames(dischargeReasons) },
      ]),
    ],
  }), [intl, isCensus, rangeLabels, dischargeReasons])

  /** Datos largos: truncados con «…» y el texto completo en el tooltip. */
  const textCell = (value: string | null | undefined): ReactElement | string => {
    const text = textOrDash(value ?? null)
    return text === EMPTY_VALUE ? text : <span className="nv-truncate nv-tip" data-pr-tooltip={text}>{text}</span>
  }

  const locationText = (row: HospitalizationRowDTO): string => [
    row.sectorName?.trim(),
    row.roomNumber?.trim() ? intl.formatMessage({ id: 'HospitalizationSummaryLocationRoom' }, { room: row.roomNumber.trim() }) : '',
    row.bedNumber?.trim() ? intl.formatMessage({ id: 'HospitalizationSummaryLocationBed' }, { bed: row.bedNumber.trim() }) : '',
  ].filter((part): part is string => !!part).join(' · ')

  const tableMinWidth = Object.entries(COLUMN_WIDTH)
    .filter(([key]) => !HIDDEN_COLUMNS.includes(key) && (!isCensus || (key !== 'end' && key !== 'dischargeReason')))
    .reduce((sum, [, width]) => sum + width, 0)

  return (
    <div className="nv-hosp-grid">
      <HospitalizationGridNotice state={state} errorMessageId="HospitalizationSummaryListError" onRetry={retry} />
      <div className="nv-hosp-table">
        <RemoteTable
          ref={tableRef}
          idTable={isCensus ? CENSUS_TABLE_ID : LIST_TABLE_ID}
          dataKey="internmentId"
          getItems={loadItems}
          params={params}
          paramsDelay={0}
          filterBar={filterBar}
          filtersType={FILTERS_TYPE}
          columnsVisible={visibleColumns}
          defaultMultiSortMeta={DEFAULT_SORT}
          refreshButton
          resizableColumns
          tableMinWidth={tableMinWidth}
          // Con error el cuerpo no dice «No hay internaciones»: lo que pasó lo dice el aviso de arriba
          notFoundText={state === 'ok' ? intl.formatMessage({ id: isCensus ? 'HospitalizationSummaryCensusEmpty' : 'HospitalizationSummaryListEmpty' }) : ' '}
          minHeight={480}
        >
          <RColumn
            field="patientName"
            header={intl.formatMessage({ id: 'HospitalizationSummaryColPatient' })}
            grow={COLUMN_WIDTH.patient}
            sortable
            filter
            filterElement={stringFilterTemplate}
            body={(row: HospitalizationRowDTO) => textCell(row.patientName)}
          />
          <RColumn
            field="serviceStart"
            header={intl.formatMessage({ id: 'HospitalizationSummaryColServiceStart' })}
            grow={COLUMN_WIDTH.start}
            sortable
            filter
            filterElement={DateFilterTemplate}
            body={(row: HospitalizationRowDTO) => formatIsoMoment(row.serviceStart)}
          />
          {!isCensus && (
            <RColumn
              field="serviceEnd"
              header={intl.formatMessage({ id: 'HospitalizationSummaryColServiceEnd' })}
              grow={COLUMN_WIDTH.end}
              sortable
              filter
              filterElement={DateFilterTemplate}
              body={(row: HospitalizationRowDTO) => (row.serviceEnd
                ? formatIsoMoment(row.serviceEnd)
                : intl.formatMessage({ id: 'HospitalizationSummaryStillAdmitted' }))}
            />
          )}
          <RColumn
            field="days"
            header={intl.formatMessage({ id: 'HospitalizationSummaryColDays' })}
            grow={COLUMN_WIDTH.days}
            className="nv-urg-center"
            headerClassName="nv-urg-center"
            sortable
            filter
            filterElement={daysFilter}
            body={(row: HospitalizationRowDTO) => (row.days == null ? EMPTY_VALUE : formatCount(intl, row.days))}
          />
          <RColumn
            field="sectorName"
            header={intl.formatMessage({ id: 'HospitalizationSummaryColLocation' })}
            grow={COLUMN_WIDTH.location}
            sortable
            body={(row: HospitalizationRowDTO) => textCell(locationText(row))}
          />
          <RColumn
            field="admissionType"
            header={intl.formatMessage({ id: 'HospitalizationSummaryColAdmissionType' })}
            grow={COLUMN_WIDTH.admissionType}
            sortable
            body={(row: HospitalizationRowDTO) => textCell(row.admissionType)}
          />
          {!isCensus && (
            <RColumn
              field="dischargeReasonId"
              sortField="dischargeReason"
              header={intl.formatMessage({ id: 'HospitalizationSummaryColDischargeReason' })}
              grow={COLUMN_WIDTH.dischargeReason}
              sortable
              filter
              filterElement={reasonFilter}
              body={(row: HospitalizationRowDTO) => textCell(row.dischargeReason)}
            />
          )}
          <RColumn
            field="financierName"
            header={intl.formatMessage({ id: 'HospitalizationSummaryColFinancier' })}
            grow={COLUMN_WIDTH.financier}
            sortable
            body={(row: HospitalizationRowDTO) => textCell(row.financierName)}
          />
          <RColumn
            field="admissionDiagnosis"
            header={intl.formatMessage({ id: 'HospitalizationSummaryColAdmissionDiagnosis' })}
            grow={COLUMN_WIDTH.diagnosis}
            sortable
            filter
            filterElement={stringFilterTemplate}
            body={(row: HospitalizationRowDTO) => textCell(row.admissionDiagnosis)}
          />
          {/* Ocultas (ver `visibleColumns`): solo para buscar y filtrar */}
          <RColumn
            field="documentNumber"
            header={intl.formatMessage({ id: 'HospitalizationSummaryColDocumentNumber' })}
            grow={COLUMN_WIDTH.document}
            filter
            filterElement={stringFilterTemplate}
          />
          <RColumn
            field="affiliateNumber"
            header={intl.formatMessage({ id: 'HospitalizationSummaryColAffiliateNumber' })}
            grow={COLUMN_WIDTH.affiliate}
            filter
            filterElement={stringFilterTemplate}
          />
          <RColumn
            field="planName"
            header={intl.formatMessage({ id: 'HospitalizationSummaryColPlan' })}
            grow={COLUMN_WIDTH.plan}
            filter
            filterElement={stringFilterTemplate}
          />
          <RColumn
            field="age"
            header={intl.formatMessage({ id: 'HospitalizationSummaryColAge' })}
            grow={COLUMN_WIDTH.age}
            filter
            filterElement={rangeFilter}
          />
        </RemoteTable>
      </div>
    </div>
  )
}
